import { useTheme, Box, Skeleton, Typography, Tooltip } from '@mui/material';
import { useState } from 'react';
import { tokens } from '../../global/theme/tokens';
import BlockIcon from '@mui/icons-material/Block';
import PaidIcon from '@mui/icons-material/Paid';
import { useTranslation } from 'react-i18next';
import MoneyLabel from '../label/MoneyLabel';
import { ExoTableToolbarItem } from '../exo/ExoTableToolbar';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import StyledTextContainer from '../preview/StyledTextContainer';
import RenderHTML from '../preview/RenderHTML';
import { numberToString } from '../special/numberConverter';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import { round } from '../special/round';
import PercentIcon from '@mui/icons-material/Percent';
import GroupWorkIcon from '@mui/icons-material/GroupWork';

const SelectPositions = ({
  positions,
  onSelect,
  isLoading = false,
  allowAll,
  columns = ['name', 'amount', 'price'],
  requirePercent,
}) => {
  const { t } = useTranslation();
  // actions
  const [checkedItems, setCheckedItems] = useState([]);

  function handleSelect(checkedItems) {
    let checkedItemsIds = checkedItems.map((el) => el.id);
    let newCheckedItems = [];

    const maxDepth = Math.max(...positions.map((el) => el.depth));

    let currentDepth = 0;
    for (let i = positions.length - 1; i >= 0; i--) {
      checkedItemsIds = checkedItems.map((el) => el.id);
      let shouldPush = false;
      if (checkedItemsIds.includes(positions[i].id)) {
        shouldPush = true;
        if (positions[i].depth > currentDepth) {
          currentDepth = positions[i].depth;
        }
      }
      if (
        currentDepth > 0 &&
        positions[i].isGroup &&
        positions[i].depth == currentDepth - 1
      ) {
        currentDepth = currentDepth - 1;
        shouldPush = true;
      }

      if (shouldPush) {
        newCheckedItems.push(positions[i]);
      } else if (checkedItemsIds.includes(positions[i].id)) {
        newCheckedItems = newCheckedItems.filter(
          (el) => el.id != positions[i].id
        );
      }
    }

    //Auto Deselect Groups
    let shouldDeselect = [];
    if (maxDepth != null && !Number.isNaN(maxDepth)) {
      shouldDeselect = Array(maxDepth + 1).fill(true);
    }
    for (let i = positions.length - 1; i >= 0; i--) {
      let newCheckedIds = newCheckedItems.map((el) => el.id);
      if (positions[i].isGroup && shouldDeselect[positions[i].depth]) {
        newCheckedItems = newCheckedItems.filter(
          (el) => el.id != positions[i].id
        );
        shouldDeselect[positions[i].depth] = true;
      }
      newCheckedIds = newCheckedItems.map((el) => el.id);

      if (
        newCheckedIds.includes(positions[i].id) &&
        positions[i].type !== 'percent'
      ) {
        shouldDeselect[positions[i].depth - 1] = false;
      }
    }

    if (requirePercent) {
      let depthsToCheck = [0];
      let hasSelectedPos = [];
      for (let i = 0; i < positions.length; i++) {
        const currentPos = positions[i];
        const newCheckedIds = newCheckedItems.map((el) => el.id);
        if (
          newCheckedIds.includes(currentPos.id) &&
          currentPos.type !== 'percent'
        ) {
          if (currentPos.depth > hasSelectedPos.length) {
            hasSelectedPos.push(true);
          }
          hasSelectedPos[currentPos.depth] = true;
        }
        if (currentPos.isGroup && newCheckedIds.includes(currentPos.id)) {
          depthsToCheck.push(currentPos.depth + 1);
        } else {
          if (
            currentPos.type === 'percent' &&
            !newCheckedIds.includes(currentPos.id) &&
            depthsToCheck.includes(currentPos.depth) &&
            hasSelectedPos[currentPos.depth]
          ) {
            newCheckedItems.push(currentPos);
          }
          if (
            currentPos.type === 'percent' &&
            newCheckedIds.includes(currentPos.id) &&
            //!depthsToCheck.includes(currentPos.depth) &&
            !hasSelectedPos[currentPos.depth]
          ) {
            newCheckedItems = newCheckedItems.filter(
              (el) => el.id != currentPos.id
            );
          }
          depthsToCheck = depthsToCheck.filter((el) => el <= currentPos.depth);
          hasSelectedPos.length = depthsToCheck.length;
        }
      }
    }

    newCheckedItems.forEach((pos) => {
      if (pos.billedTotal && pos.type !== 'percent') {
        if (pos.price != 0) {
          pos.billedAmount = round(Number(pos.billedTotal) / Number(pos.price)); //rounded
        }
      }
    });

    newCheckedItems = newCheckedItems.reverse();

    setCheckedItems(newCheckedItems);
    onSelect(newCheckedItems);
  }

  const handleClick = (item) => {
    if (!allowAll && (item.status === 1 || item.status === 2)) return;
    if (item.type === 'percent' || item.isGroup) return;
    var newCheckedItems = [];
    if (checkItemIsSelected(item)) {
      newCheckedItems = checkedItems.filter((obj) => obj.id !== item.id);
    } else {
      newCheckedItems = [...checkedItems, item];
    }

    setCheckedItems(newCheckedItems);
    handleSelect(newCheckedItems);
  };

  function checkItemIsSelected(item) {
    const requestedItem = checkedItems.find((obj) => obj.id === item.id);

    return Boolean(requestedItem);
  }

  function selectAll(withOptional = false) {
    if (allowAll) {
      setCheckedItems(positions);
      return handleSelect(positions);
    }
    const newSelectedPositions = positions.filter(
      (obj) => obj.status !== 1 && obj.status !== 2 && !obj.isOptional
    );
    setCheckedItems(newSelectedPositions);
    handleSelect(newSelectedPositions);
  }

  return (
    <Box className="w-full flex flex-col gap-2">
      {isLoading ? (
        <>
          {[...Array(30)].map((e, i) => (
            <Skeleton
              key={'pos-skeleton-' + i}
              height={37}
              sx={{ transform: 'scale(1)', borderRadius: '9999px' }}
            />
          ))}
        </>
      ) : (
        <>
          <ExoTableToolbarItem
            text={t('Select everything except optional')}
            icon={<DoneAllIcon />}
            onClick={selectAll}
          />
          {positions.map((element) => (
            <Position
              allowAll={allowAll}
              key={'pos-' + element.id}
              element={element}
              onClick={handleClick}
              selected={checkItemIsSelected(element)}
              columns={columns}
            />
          ))}
        </>
      )}
    </Box>
  );
};

const Position = ({ element, onClick, selected, allowAll, columns }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();

  const usePercent = element.type == 'hoaiPhase' || element.type == 'percent';

  let billedAmount = null;

  if (element.billedTotal && element.type !== 'percent') {
    billedAmount = round(element.billedTotal / element.price); //rounded
  } else if (element.type !== 'percent') {
    billedAmount = 0;
  }

  return (
    <Box className="flex">
      {element.depth != null &&
        Array.from({ length: element.depth }).map((_, i) => (
          <Box
            key={i}
            style={{
              width: '20px',
              borderLeft: '1px dashed lightgrey',
            }}
          />
        ))}
      <Box
        onClick={() => onClick(element)}
        className="px-4 flex justify-between items-center rounded-lg w-full"
        sx={{
          py: element.isGroup ? '9px' : 0,
          backgroundColor: selected ? colors.info + '30' : colors.bgInput,
          border: selected
            ? `1px solid ${colors.info}80`
            : `1px solid transparent`,
          cursor:
            (!allowAll && element.status) ||
            element.type == 'percent' ||
            element.isGroup
              ? 'not-allowed'
              : 'pointer',
        }}
      >
        {element.type === 'percent' && (
          <PercentIcon className="mr-3" fontSize="small" />
        )}
        {element.isGroup && <GroupWorkIcon className="mr-3" fontSize="small" />}
        {columns.includes('name') && <PositionName element={element} />}
        <Box className="flex items-center gap-0">
          {columns.includes('amount') && !element.isGroup && (
            <Box className="flex flex-col">
              <Box
                className="monospace text-right min-w-fit flex items-start h-full w-28"
                sx={{
                  textDecoration:
                    !allowAll && (element.status == 1 || element.status == 2)
                      ? 'line-through'
                      : 'none',
                }}
              >
                <Tooltip
                  title={element.type !== 'percent' ? t('Open quantity') : null}
                >
                  {`${numberToString(
                    billedAmount
                      ? (element.amount - billedAmount) * (usePercent ? 100 : 1)
                      : element.amount * (usePercent ? 100 : 1)
                  )} ${usePercent ? '%' : element.unit || ''}`}
                </Tooltip>
              </Box>
              {element.type !== 'percent' && (
                <Box
                  className="monospace text-right min-w-fit flex items-start h-full w-28"
                  sx={{
                    textDecoration:
                      !allowAll && (element.status == 1 || element.status == 2)
                        ? 'line-through'
                        : 'none',
                  }}
                >
                  <Tooltip title={t('Total quantity')}>
                    <Typography fontSize={12} color={colors.grey[600]}>
                      {`${numberToString(
                        element.amount * (usePercent ? 100 : 1)
                      )} ${usePercent ? '%' : element.unit || ''}`}
                    </Typography>
                  </Tooltip>
                </Box>
              )}
            </Box>
          )}
          {columns.includes('price') && !element.isGroup && (
            <PositionPrice element={element} allowAll={allowAll} />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export const PositionName = ({ element }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();

  return (
    <Box className="block w-full">
      <Box className="flex flex-row justify-start gap-2">
        <Typography className="flex gap-2">
          {element.isOptional && '('}
          {element.name}
          {element.isOptional && ')'}
        </Typography>
        <Box className="flex items-center gap-2">
          {element.deliveredAt && (
            <Tooltip title={t('delivered')}>
              <LocalShippingIcon sx={{ color: colors.greenAccent[500] }} />
            </Tooltip>
          )}
          {element.status == 1 && (
            <Tooltip title={t('settled')}>
              <PaidIcon sx={{ color: colors.greenAccent[500] }} />
            </Tooltip>
          )}
          {element.status == 2 && (
            <Tooltip title={t('canceled')}>
              <BlockIcon sx={{ color: colors.redAccent[500] }} />
            </Tooltip>
          )}
        </Box>
      </Box>

      <Box className="flex">
        {element.image && (
          <img
            src={element.image.thumbnailUrl}
            className="w-20 object-contain object-top hidden md:block"
          />
        )}
        {element.description && (
          <StyledTextContainer
            fullWidth
            backgroundColor="transparent"
            className="pl-2"
          >
            <RenderHTML HTML={element.description} />
          </StyledTextContainer>
        )}
      </Box>
    </Box>
  );
};

const PositionPrice = ({ element, allowAll }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);

  return (
    <Box className="flex flex-col">
      <Box
        className="monospace min-w-fit flex items-start justify-end h-full w-28"
        sx={{
          textDecoration:
            !allowAll && (element.status == 1 || element.status == 2)
              ? 'line-through'
              : 'none',
        }}
      >
        <Tooltip title={t('Open amount')}>
          {element.isOptional && '('}
          <MoneyLabel
            money={
              element.billedTotal && element.type !== 'percent'
                ? round(element.amount * element.price, 2) -
                    element.billedTotal ==
                  0
                  ? 0
                  : round(element.amount * element.price, 2) -
                    element.billedTotal
                : round(element.amount * element.price, 2) == 0
                ? 0
                : round(element.amount * element.price, 2)
            }
          />
          {element.isOptional && ')'}
        </Tooltip>
      </Box>
      <Box
        className="monospace min-w-fit flex items-start justify-end h-full w-28"
        sx={{
          textDecoration:
            !allowAll && (element.status == 1 || element.status == 2)
              ? 'line-through'
              : 'none',
        }}
      >
        <Tooltip title={t('Total Amount')}>
          <Typography fontSize={12} color={colors.grey[600]}>
            <MoneyLabel money={round(element.amount * element.price, 2)} />
          </Typography>
        </Tooltip>
      </Box>
    </Box>
  );
};
export default SelectPositions;
